import {
  AccessorKeyColumnDef,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  Header,
  RowData,
  Updater,
  useReactTable,
} from '@tanstack/react-table';

import { Button } from '../Button';
import { IconArrowNarrowDown, IconArrowNarrowUp } from '@tabler/icons-react';
import { cn } from '../../lib/utils';
import { handleExport } from './export';
import {
  Table as TablePrimitive,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './components/Table';
import { Tooltip } from '../Tooltip';
import { DownloadIcon } from 'lucide-react';
import type {
  TableState,
  ColumnDefMeta,
  TableProps,
  TableRef,
  TableComponent,
  TableContextType,
} from './types';

import { Filter } from './components/Filter/Filter';
import { TableContext } from './hooks/useTableContext';
import {
  forwardRef,
  Fragment,
  Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { ConditionalWrapper } from '../../lib/ConditionalWrapper';
import { arrIncludesArrayItem, numberInRange } from './filterFn';
import { getAlignmentClass, getFlexAlignmentClass } from './utils';
import useI18n from '../../hooks/useI18n';

function resolveUpdater<T>(updater: Updater<T>, old: T): T {
  return typeof updater === 'function'
    ? (updater as (old: T) => T)(old)
    : updater;
}

function TableInner<TData extends RowData>(
  {
    children,
    columns,
    data,
    id,
    renderExpandable = () => null,
    disableExport = false,
    exportOptions,
    className,
    containerClassName,
    pageSize = 100,
    hideFooter = false,
    globalFilterFn = 'includesString',
    onStateChange,
    initialState,
    expanded,
    ...rest
  }: TableProps<TData>,
  ref: Ref<TableRef<TData>>,
) {
  const { t } = useI18n();
  const containerRef = useRef<HTMLDivElement>(null);

  exportOptions = { filename: 'export', ...exportOptions };

  const tableId = useMemo(() => {
    return (
      `${window.location.pathname}_${id}_` +
      columns
        .map((column) => (column as AccessorKeyColumnDef<TData>)?.accessorKey)
        .sort()
        .join('_')
    );
  }, [id, columns]);

  const state = {
    columnFilters: [],
    sorting: [
      {
        id: (columns[0]?.id ??
          (columns[0] as AccessorKeyColumnDef<TData, unknown>)
            ?.accessorKey) as string,
        desc: true,
      },
    ],
    columnVisibility: {},
    ...initialState,
  };

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize,
  });
  const [globalFilter, setGlobalFilter] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const [tableState, setTableState] = useState<TableState>(() => {
    try {
      const storedState = localStorage.getItem(tableId);
      if (!storedState) {
        return state;
      }

      const tableState = JSON.parse(storedState) as TableState;
      // column filters defined inside initialState have higher precedence
      const filters = [...tableState.columnFilters, ...state.columnFilters];
      const columnFilters = Array.from(
        new Map(filters.map((item) => [item.id, item])).values(),
      );
      return { ...tableState, columnFilters };
    } catch (err) {
      localStorage.removeItem(tableId);
      console.error(
        `Couldn't load table ${tableId} state from localStorage: `,
        err,
      );
      return state;
    }
  });

  useEffect(() => {
    localStorage.setItem(tableId, JSON.stringify(tableState));
  }, [tableId, tableState]);

  useEffect(() => {
    onStateChange?.(tableState);
  }, [onStateChange, tableState]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isFullscreen]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),

    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: (updater) => {
      if (containerRef?.current) {
        containerRef.current.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }
      setPagination(updater);
    },

    getSortedRowModel: getSortedRowModel(),
    onSortingChange: (updater) =>
      setTableState((prev) => ({
        ...prev,
        sorting: resolveUpdater(updater, prev.sorting ?? []),
      })),
    onGlobalFilterChange: setGlobalFilter,
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: globalFilterFn || 'includesString',
    onColumnFiltersChange: (updater) =>
      setTableState((prev) => ({
        ...prev,
        columnFilters: resolveUpdater(updater, prev.columnFilters ?? []),
      })),
    onColumnVisibilityChange: (updater) =>
      setTableState((prev) => ({
        ...prev,
        columnVisibility: resolveUpdater(updater, prev.columnVisibility ?? {}),
      })),

    getExpandedRowModel: getExpandedRowModel(),
    onExpandedChange: setExpandedRows,

    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),

    state: {
      ...tableState,
      pagination,
      globalFilter,
      expanded: expandedRows,
    },

    filterFns: {
      arrIncludesArrayItem,
      numberInRange,
    },
    ...rest,
  });

  useEffect(() => {
    if (expanded) {
      setExpandedRows(expanded);
    }
  }, [expanded]);

  const exportTableData = useCallback(() => {
    if (disableExport) return;
    handleExport(table.getPrePaginationRowModel().rows, columns, exportOptions);
  }, [columns, disableExport, exportOptions, table]);

  useImperativeHandle(
    ref,
    (): TableRef<TData> => ({
      table,
      tableId,
      exportTableData,
    }),
    [table, tableId, exportTableData],
  );

  const contextValue: TableContextType<TData> = {
    isFullscreen,
    setIsFullscreen,
    globalFilter,
    setGlobalFilter,
    tableState,
    table,
  };

  function sort(header: Header<TData, unknown>) {
    header.column.toggleSorting(
      header.column.getIsSorted()
        ? header.column.getIsSorted() === 'asc'
        : true,
    );
  }

  const tableContent = (
    <div
      className={cn(
        'eye:flex eye:flex-col',
        isFullscreen
          ? 'eye:grow-y eye:fixed eye:inset-[0] eye:z-10000 eye:bg-white eye:p-xs'
          : 'eye:w-full',
      )}
    >
      {children}
      <div
        ref={containerRef}
        className={cn(
          'eye:rounded-md eye:border eye:border-grey-40',
          !isFullscreen && 'eye:max-h-[70dvh]',
          'eye:w-full eye:flex-1 eye:overflow-auto',
          containerClassName,
        )}
      >
        <TablePrimitive className={cn('eye:w-full', className)}>
          <TableHeader className={'eye:sticky eye:top-[0] eye:z-10'}>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead
                    style={{
                      width: header.column.columnDef.size ?? header.getSize(),
                      minWidth:
                        header.column.columnDef.minSize ?? header.getSize(),
                      maxWidth:
                        header.column.columnDef.maxSize ?? header.getSize(),
                    }}
                    key={header.id}
                  >
                    <span
                      className={cn(
                        'eye:flex eye:w-full eye:items-center eye:overflow-hidden',
                        getFlexAlignmentClass(
                          (header.column.columnDef?.meta as ColumnDefMeta)
                            ?.align,
                        ),
                      )}
                    >
                      <ConditionalWrapper
                        condition={header.column.getCanSort()}
                        wrapper={(children) => (
                          <Tooltip
                            content={`${header.column.columnDef.header}
                              ${
                                header.column.getIsSorted()
                                  ? header.column.getIsSorted() === 'asc'
                                    ? `(${t('sortedAsc')})`
                                    : `(${t('sortedDesc')})`
                                  : ''
                              }
                              `}
                          >
                            <span
                              tabIndex={0}
                              className='eye:flex eye:min-w-0 eye:cursor-pointer eye:items-center eye:truncate'
                              role='button'
                              aria-label={
                                t('sortBy') +
                                ': ' +
                                header.column.columnDef.header
                              }
                              onClick={() => sort(header)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  // prevents page scroll on Space
                                  e.preventDefault();
                                  sort(header);
                                }
                              }}
                            >
                              <span className='eye:truncate'>{children}</span>
                              {header.column.getIsSorted() ? (
                                header.column.getIsSorted() === 'asc' ? (
                                  <IconArrowNarrowUp
                                    className='eye:shrink-0'
                                    aria-label={t('sortAscending') as string}
                                  />
                                ) : (
                                  <IconArrowNarrowDown
                                    className='eye:shrink-0'
                                    aria-label={t('sortDescending') as string}
                                  />
                                )
                              ) : null}
                            </span>
                          </Tooltip>
                        )}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </ConditionalWrapper>
                    </span>
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getPaginationRowModel().rows.length ? (
              table.getPaginationRowModel().rows.map((row) => (
                <Fragment key={row.id}>
                  <TableRow
                    className='eye:bg-white'
                    data-state={row.getIsSelected() && 'selected'}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        className={cn(
                          getAlignmentClass(
                            (cell.column.columnDef?.meta as ColumnDefMeta)
                              ?.align,
                          ),
                        )}
                        style={{
                          width:
                            cell.column.columnDef.size ?? cell.column.getSize(),
                          minWidth:
                            cell.column.columnDef.minSize ??
                            cell.column.getSize(),
                          maxWidth:
                            cell.column.columnDef.maxSize ??
                            cell.column.getSize(),
                        }}
                        key={cell.id}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                  {renderExpandable(row)}
                </Fragment>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className='eye:p-32 eye:text-center'
                >
                  {t('noResultsFound')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </TablePrimitive>
      </div>

      {!hideFooter && (
        <div
          className={cn(
            'eye:my-8 eye:flex eye:flex-wrap eye:items-center eye:justify-between eye:gap-y-12',
            isFullscreen && 'eye:px-8 eye:pb-8',
          )}
        >
          <div className='eye:flex-[0_1_auto]'>
            <Tooltip content={t('exportAllRows')}>
              <Button
                disabled={disableExport}
                variant='ghost'
                size='icon'
                onClick={() => exportTableData()}
              >
                <DownloadIcon aria-label={t('exportAllRows')} />
              </Button>
            </Tooltip>
          </div>

          {!!table.getPrePaginationRowModel().rows.length && (
            <span className={'eye:body-s eye:mx-auto eye:flex-[0_1_auto]'}>
              {t('tableRowsCounter', {
                from:
                  1 +
                  table.getState().pagination.pageSize *
                    (table.getState().pagination.pageIndex ?? 0),
                to: Math.min(
                  table.getPrePaginationRowModel().rows.length,
                  table.getState().pagination.pageSize *
                    ((table.getState().pagination.pageIndex ?? 0) + 1),
                ),
                total: table.getPrePaginationRowModel().rows.length,
              })}
              {' - '}
              {t('tablePage', {
                from: table.getState().pagination.pageIndex + 1,
                total: table.getPageCount(),
              })}
            </span>
          )}

          {table.getPageCount() > 1 && (
            <div className='eye:space-x-2 eye:flex eye:min-w-full eye:justify-center eye:gap-12 eye:sm:min-w-auto'>
              <Button
                variant='outline'
                size='sm'
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              >
                {t('previousPage')}
              </Button>
              <Button
                variant='outline'
                size='sm'
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              >
                {t('nextPage')}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <TableContext.Provider value={contextValue as TableContextType<unknown>}>
      {isFullscreen ? createPortal(tableContent, document.body) : tableContent}
    </TableContext.Provider>
  );
}

const TableBase = forwardRef(TableInner) as unknown as TableComponent;

TableBase.Filter = Filter;

export const Table = TableBase;
